import { FincasPlataformaService, FincaFichaPlataformaItem, LoteFichaPlataformaItem } from "../../generated/grpc/plataforma_terceros_fincas_pb";
import { FincaFichaResponse, LoteFichaResponse } from "../../response/terceros/FincaFichaResponse";
import { OrtomosaicoEstado } from "../../response/terceros/FincaResponse";
import { grpcCall, grpcClient } from "../grpc/grpcCall";
import { wkbToMultiPoint, wkbToPolygon } from "../../utils/wkbUtils";

/** Ficha de finca (`FincasPlataformaService.GetFicha`, gRPC): la finca con sus geometrías y sus lotes. */
export class FincaGetByIdService {
  static readonly instance = new FincaGetByIdService();
  private constructor() { }

  /**
   * Ficha vista desde el cliente dueño (define el nombre de la finca y "plataforma activa").
   * Lanza `NotFoundError` si la finca no existe, está eliminada o el cliente no es dueño activo.
   */
  async getById(id: string, clienteId: string): Promise<FincaFichaResponse> {
    const response = await grpcCall(() =>
      grpcClient(FincasPlataformaService).getFicha({ fincaId: id, clienteId }),
    );
    if (!response.finca) throw new Error("Respuesta sin finca");
    return toFincaFichaResponse(response.finca, response.lotes);
  }
}

/** Nombre del tipo de finca a partir de su código (0 Agricultura, 1 Ganadería). */
export function nombreTipoFinca(tipoFinca: number): string | null {
  return tipoFinca === 0 ? "Agricultura" : tipoFinca === 1 ? "Ganadería" : null;
}

/** Mensaje gRPC de la ficha (y sus lotes) → `FincaFichaResponse`; decodifica las geometrías WKB. */
function toFincaFichaResponse(f: FincaFichaPlataformaItem, lotes: LoteFichaPlataformaItem[]): FincaFichaResponse {
  return {
    id: f.id,
    isEliminado: false,
    nombreFinca: f.nombreFinca,
    municipioNombre: f.municipioNombre ?? null,
    departamentoNombre: f.departamentoNombre ?? null,
    departamentoRegionNombre: f.departamentoRegionNombre ?? null,
    hectareasFinca: f.hectareasFinca,
    hectareasProductivas: f.hectareasProductivas,
    tipoFinca: f.tipoFinca,
    tipo: nombreTipoFinca(f.tipoFinca),
    plataformaActiva: f.plataformaActiva,
    cultivos: f.cultivos,
    zipOrtomosaico: f.zipOrtomosaico ?? null,
    uriOrtomosaico: f.uriOrtomosaico ?? null,
    ortomosaicoEstado: f.ortomosaicoEstado as OrtomosaicoEstado,
    ortomosaicoError: f.ortomosaicoError ?? null,
    sateliteAreaGeografica: f.sateliteAreaGeografica?.length ? wkbToPolygon(f.sateliteAreaGeografica) : null,
    conteoMultiPoint: f.conteoMultiPoint?.length ? wkbToMultiPoint(f.conteoMultiPoint) : null,
    lotes: lotes.map(toLoteFichaResponse),
  };
}

/** Mensaje gRPC del lote → `LoteFichaResponse`. */
function toLoteFichaResponse(l: LoteFichaPlataformaItem): LoteFichaResponse {
  return {
    id: l.id,
    nombreLote: l.nombreLote,
    areaHectareas: l.areaHectareas,
    areaCuadras: l.areaCuadras,
    isAreaProductiva: l.isAreaProductiva,
    cultivo: l.cultivoNombre !== undefined ? { nombre: l.cultivoNombre } : null,
    tipoMonitoreo: l.tipoMonitoreo ?? null,
    areaGeograficaPolygon: l.areaGeografica.length
      ? wkbToPolygon(l.areaGeografica)
      : { type: "Polygon", coordinates: [] },
  };
}
